import _lodash from 'lodash';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EmailMessage } from '@/models/email/email-message.model';
import { AgentModel } from '@/models/agent.model';
import { PartialContactModel } from '@/models/contact.model';
import { InteractionDetailModel } from '@/models/omni-channel/interaction-model';

export interface EmailCardItemState {
  expand: boolean;
  sendEmailFlag: boolean;
  emailMessage: EmailMessage[];
  showError: boolean;
  interactionDetail: InteractionDetailModel;
}

interface EmailCardState {
  interactionMapper: Record<string, EmailCardItemState>;
  agentUserMapper: Record<string, AgentModel>;
  fromToCcBccUserMapper: Record<string, PartialContactModel>;
  emailTouchPointsMapper: Record<string, PartialContactModel>;
}

type InteractionMapperAction<T> = {
  interactionId: string;
  value: T;
};

const initialState: EmailCardState = {
  interactionMapper: {},
  agentUserMapper: {},
  fromToCcBccUserMapper: {},
  emailTouchPointsMapper: {},
};

export const initialInteraction: EmailCardItemState = {
  expand: false,
  sendEmailFlag: false,
  emailMessage: [],
  showError: false,
  interactionDetail: {} as InteractionDetailModel,
};

const emailCard = createSlice({
  name: 'email-card',
  initialState,
  reducers: {
    setInitialInteraction: (
      state,
      action: PayloadAction<{ interactionId: string; defaultExpand: boolean; defaultShowSendEmail: boolean }>,
    ) => {
      const { interactionId, defaultExpand, defaultShowSendEmail } = action.payload;
      !state.interactionMapper[interactionId] &&
        (state.interactionMapper[interactionId] = {
          ...initialInteraction,
          expand: defaultExpand,
          sendEmailFlag: defaultShowSendEmail,
        });
    },
    setExpand: (state, action: PayloadAction<InteractionMapperAction<EmailCardItemState['expand']>>) => {
      const { interactionId, value } = action.payload;
      state.interactionMapper[interactionId].expand = value;
    },
    setSendEmailFlag: (state, action: PayloadAction<InteractionMapperAction<EmailCardItemState['sendEmailFlag']>>) => {
      const { interactionId, value } = action.payload;
      state.interactionMapper[interactionId].sendEmailFlag = value;
    },
    setShowError: (state, action: PayloadAction<InteractionMapperAction<EmailCardItemState['showError']>>) => {
      const { interactionId, value } = action.payload;
      state.interactionMapper[interactionId].showError = value;
    },
    setInteractionDetail: (
      state,
      action: PayloadAction<InteractionMapperAction<EmailCardItemState['interactionDetail']>>,
    ) => {
      const { interactionId, value } = action.payload;
      state.interactionMapper[interactionId].interactionDetail = value;
    },
    setEmailMessage: (state, action: PayloadAction<InteractionMapperAction<EmailCardItemState['emailMessage']>>) => {
      const { interactionId, value } = action.payload;
      state.interactionMapper[interactionId].emailMessage = _lodash.uniqBy(
        value.filter((item) => item.message_id),
        'message_id',
      );
    },
    setAgentUserMapper: (state, action: PayloadAction<AgentModel[]>) => {
      if (!action.payload.length) return;
      const agentUserMapperCache: Record<string, AgentModel> = {};
      action.payload.forEach((record) => (agentUserMapperCache[record.id] = record));
      state.agentUserMapper = { ...state.agentUserMapper, ...agentUserMapperCache };
    },
    setFromToCcBccUserMapper: (state, action: PayloadAction<PartialContactModel[]>) => {
      if (!action.payload.length) return;
      const fromToCcBccUserMapper: Record<string, PartialContactModel> = {};
      action.payload.forEach((record) => record.email && (fromToCcBccUserMapper[record.email] = record));
      state.fromToCcBccUserMapper = { ...state.fromToCcBccUserMapper, ...fromToCcBccUserMapper };
    },

    setEmailTouchPointsMapper: (state, action: PayloadAction<PartialContactModel[]>) => {
      if (!action.payload.length) return;
      const emailTouchPointsMapper: Record<string, PartialContactModel> = {};
      action.payload.forEach((record) => record.email && (emailTouchPointsMapper[record.email] = record));
      state.emailTouchPointsMapper = { ...state.emailTouchPointsMapper, ...emailTouchPointsMapper };
    },
  },
});

export const {
  setInitialInteraction,
  setExpand,
  setSendEmailFlag,
  setShowError,
  setInteractionDetail,
  setEmailMessage,
  setAgentUserMapper,
  setFromToCcBccUserMapper,
  setEmailTouchPointsMapper,
} = emailCard.actions;
export default emailCard.reducer;
